import type { ScreenShareSourceMode } from './CompanionVision';

/**
 * Tab set, revised 2026-08-14 (operator request).
 *
 * #2851 (IA slice 5) had folded share, live and library into a `media` tab
 * alongside recording and broadcast, on the reasoning that a share, a channel
 * and a recording are all "media". In use that grouped the things a host puts
 * ON the stage with the things the room sends OUT of it, and left Stage holding
 * only activities — so composing a stage meant working in two tabs.
 *
 * The split is now by DIRECTION, which is what a host is actually doing:
 *
 * - `mode`      — active mode and its workspace: presentation/program/agenda.
 * - `stage`     — general stage sources: activities, share, live and library.
 * - `output`    — output only: recording, streaming, simulcast destinations.
 * - `audience`  — promotions, questions, polls, moderation.
 * - `companion` — the AI companion: which one, and what it can perceive.
 *                 Promoted out of Settings, where it was buried under
 *                 lower-frequency room configuration.
 * - `room`      — room defaults, help, diagnostics. Renamed from `settings`:
 *                 "Settings" said nothing about what was inside it once the
 *                 companion controls left.
 *
 * The ORDER of this list is the display order, in every room type. It used to
 * come from each lane's `tabOrder`, so the same tabs sat in different places
 * depending on a mode the host had usually never chosen — Casual put Output last,
 * every other lane put it second. With the lane selector gone (2026-08-14) the
 * order is simply canonical, the way `HOST_PANEL_SUBTABS` already was.
 */
export const HOST_PANEL_TABS = ['mode', 'stage', 'audience', 'output', 'companion', 'room'] as const;

export type HostPanelTabId = typeof HOST_PANEL_TABS[number];

export const HOST_EXPERIENCE_MODES = [
  'casual_conversation',
  'presentation',
  'gaming',
  'watching_sports',
] as const;

export type HostExperienceMode = typeof HOST_EXPERIENCE_MODES[number];

export const HOST_PANEL_CAPABILITIES = [
  'supportsSharedStageMedia',
  'supportsRoomMusic',
  'supportsRecording',
  'supportsBroadcast',
  'supportsProgramFeed',
  'supportsPresentationOverlay',
  'supportsWebStage',
  'supportsGameplayShare',
  'supportsScreenShare',
  'supportsRoomActivities',
  'supportsAudienceLayer',
  'supportsPromotionLadder',
  'supportsModerationQueue',
  'supportsCompanionAgents',
  'supportsCompanionInterruptOnlyPtt',
  'supportsLocalSpeakerCamControl',
  'supportsReactions',
  'supportsQuestions',
  'supportsPolls',
  'supportsVoting',
] as const;

export type HostPanelCapability = typeof HOST_PANEL_CAPABILITIES[number];

export type HostPanelCapabilitySet = Partial<Record<HostPanelCapability, boolean>>;

export function isHostPanelCapabilityEnabled(
  capabilities: HostPanelCapabilitySet | undefined,
  capability?: HostPanelCapability,
): boolean {
  return capability ? capabilities?.[capability] === true : true;
}

// #4892 — no recording or broadcast: they are what separates the engines, and record/stream lives
// in Make a show (townhall). The room-stage pipeline that feeds a TV display is not a capability
// here and is untouched.
export const STANDARD_HOST_PANEL_CAPABILITIES = {
  supportsSharedStageMedia: true,
  supportsRoomMusic: true,
  supportsGameplayShare: true,
  supportsScreenShare: true,
  supportsRoomActivities: true,
  supportsCompanionAgents: true,
  supportsLocalSpeakerCamControl: true,
  supportsReactions: true,
} as const satisfies HostPanelCapabilitySet;

export const TOWNHALL_HOST_PANEL_CAPABILITIES = {
  supportsSharedStageMedia: true,
  supportsRoomMusic: true,
  // #2838 — townhall runs room-native activities like any other room: voice
  // LOAD_ACTIVITY and any `activate-module` broadcast both reach it. Omitting
  // this capability filtered the family out of the panel, so activities were
  // startable by voice and invisible to the host.
  supportsRoomActivities: true,
  supportsRecording: true,
  supportsBroadcast: true,
  supportsProgramFeed: true,
  supportsPresentationOverlay: true,
  supportsWebStage: true,
  supportsGameplayShare: true,
  supportsScreenShare: true,
  supportsAudienceLayer: true,
  supportsPromotionLadder: true,
  supportsModerationQueue: true,
  supportsCompanionAgents: true,
  supportsCompanionInterruptOnlyPtt: true,
  supportsReactions: true,
  supportsQuestions: true,
  supportsPolls: true,
  supportsVoting: true,
} as const satisfies HostPanelCapabilitySet;

export type HostPanelCapabilityRoomInput = {
  room_type?: string | null;
  roomType?: string | null;
} | null | undefined;

export function getHostPanelCapabilitiesForRoom(
  room: HostPanelCapabilityRoomInput,
): HostPanelCapabilitySet {
  const roomType = room?.room_type ?? room?.roomType;
  if (roomType === 'townhall') return TOWNHALL_HOST_PANEL_CAPABILITIES;
  if (roomType && roomType !== 'standard') return { ...STANDARD_HOST_PANEL_CAPABILITIES, supportsRoomMusic: false };
  return STANDARD_HOST_PANEL_CAPABILITIES;
}

export type HostSourceFamily =
  | 'people'
  | 'room_activity'
  | 'desktop_share'
  | 'gameplay_share'
  | 'live_stream'
  | 'shared_stage_media'
  | 'presentation'
  | 'web_stage'
  | 'companion';

export interface HostSourceFamilyDefinition {
  id: HostSourceFamily;
  label: string;
  requiredCapability?: HostPanelCapability;
}

export const HOST_SOURCE_FAMILY_DEFINITIONS: Record<HostSourceFamily, HostSourceFamilyDefinition> = {
  people: {
    id: 'people',
    label: 'People',
  },
  room_activity: {
    id: 'room_activity',
    label: 'Room Activity',
    requiredCapability: 'supportsRoomActivities',
  },
  desktop_share: {
    id: 'desktop_share',
    label: 'Desktop Share',
    requiredCapability: 'supportsScreenShare',
  },
  gameplay_share: {
    id: 'gameplay_share',
    label: 'Gameplay Share',
    requiredCapability: 'supportsGameplayShare',
  },
  live_stream: {
    id: 'live_stream',
    label: 'Live Stream',
    requiredCapability: 'supportsSharedStageMedia',
  },
  shared_stage_media: {
    id: 'shared_stage_media',
    label: 'Stage Media',
    requiredCapability: 'supportsSharedStageMedia',
  },
  presentation: {
    id: 'presentation',
    label: 'Presentation',
    requiredCapability: 'supportsPresentationOverlay',
  },
  web_stage: {
    id: 'web_stage',
    label: 'Web Stage',
    requiredCapability: 'supportsWebStage',
  },
  companion: {
    id: 'companion',
    label: 'Companion',
    requiredCapability: 'supportsCompanionAgents',
  },
};

export type HostSourceStandardStageSection = 'people' | 'shares' | 'media' | 'activities';
/**
 * #2853 — one field, one vocabulary.
 *
 * This was `standardMediaPickerTab` ('epg' | 'private' | 'youtube') plus
 * `townhallStageMediaTab` ('youtube' | 'live' | 'private') — two names for the
 * same decision, disagreeing on what to call the same destination ('epg' vs
 * 'live'). The standard one was read by nothing at all.
 */
export type HostSourceMediaPickerTab = 'youtube' | 'live' | 'private';
export type HostSourceGameCategoryFocus = 'all' | 'featured';
export type HostSourceTownhallSidebarTarget = 'agenda' | 'presentations' | 'stage-media' | 'share' | 'config';
export type HostSourceActivationKind = 'focus' | 'start_share';

export interface HostSourceDefaultActivity {
  moduleId: string;
  label: string;
  customData?: Record<string, unknown>;
}

export interface HostSourceFamilyPreset {
  id: HostSourceFamily;
  standardStageSection: HostSourceStandardStageSection;
  activationKind: HostSourceActivationKind;
  mediaPickerTab?: HostSourceMediaPickerTab;
  standardGameCategoryFocus?: HostSourceGameCategoryFocus;
  standardDefaultActivity?: HostSourceDefaultActivity;
  townhallSidebarTarget?: HostSourceTownhallSidebarTarget;
}

export const HOST_SOURCE_FAMILY_PRESETS = {
  people: {
    id: 'people',
    standardStageSection: 'people',
    activationKind: 'focus',
    townhallSidebarTarget: 'agenda',
  },
  room_activity: {
    id: 'room_activity',
    standardStageSection: 'activities',
    // #2838 — every other family declared a townhall target; without one
    // nothing could route to activities in a townhall panel.
    townhallSidebarTarget: 'share',
    activationKind: 'focus',
    standardGameCategoryFocus: 'featured',
    standardDefaultActivity: {
      moduleId: 'shoutit',
      label: 'Shout It!',
      customData: {
        totalRounds: 5,
        roundTimeLimit: 30,
      },
    },
  },
  desktop_share: {
    id: 'desktop_share',
    standardStageSection: 'shares',
    activationKind: 'start_share',
    townhallSidebarTarget: 'share',
  },
  gameplay_share: {
    id: 'gameplay_share',
    standardStageSection: 'shares',
    activationKind: 'start_share',
    townhallSidebarTarget: 'share',
  },
  live_stream: {
    id: 'live_stream',
    standardStageSection: 'media',
    activationKind: 'focus',
    mediaPickerTab: 'live',
    townhallSidebarTarget: 'stage-media',
  },
  shared_stage_media: {
    id: 'shared_stage_media',
    standardStageSection: 'media',
    activationKind: 'focus',
    mediaPickerTab: 'private',
    townhallSidebarTarget: 'stage-media',
  },
  presentation: {
    id: 'presentation',
    standardStageSection: 'media',
    activationKind: 'focus',
    mediaPickerTab: 'youtube',
    townhallSidebarTarget: 'presentations',
  },
  web_stage: {
    id: 'web_stage',
    standardStageSection: 'media',
    activationKind: 'focus',
    mediaPickerTab: 'youtube',
    townhallSidebarTarget: 'presentations',
  },
  companion: {
    id: 'companion',
    standardStageSection: 'people',
    activationKind: 'focus',
    townhallSidebarTarget: 'config',
  },
} as const satisfies Record<HostSourceFamily, HostSourceFamilyPreset>;

export const HOST_STAGE_LAYOUTS = [
  'conversation',
  'presentation',
  'game',
  'watch_party',
] as const;

export type HostStageLayoutId = typeof HOST_STAGE_LAYOUTS[number];

export const HOST_STAGE_LAYOUT_LABELS = {
  conversation: 'Conversation',
  presentation: 'Presentation',
  game: 'Game',
  watch_party: 'Watch Party',
} as const satisfies Record<HostStageLayoutId, string>;

export interface HostStageLayoutBehavior {
  id: HostStageLayoutId;
  label: string;
  pipDockVisible: boolean;
  gridViewEnabled: boolean;
  speakerCamEnabled: boolean;
}

export const HOST_STAGE_LAYOUT_BEHAVIORS = {
  conversation: {
    id: 'conversation',
    label: HOST_STAGE_LAYOUT_LABELS.conversation,
    pipDockVisible: true,
    gridViewEnabled: false,
    // #3677 — Speaker Cam is opt-in for every layout, conversation included.
    speakerCamEnabled: false,
  },
  presentation: {
    id: 'presentation',
    label: HOST_STAGE_LAYOUT_LABELS.presentation,
    pipDockVisible: true,
    gridViewEnabled: false,
    speakerCamEnabled: false,
  },
  game: {
    id: 'game',
    label: HOST_STAGE_LAYOUT_LABELS.game,
    pipDockVisible: true,
    gridViewEnabled: false,
    speakerCamEnabled: false,
  },
  watch_party: {
    id: 'watch_party',
    label: HOST_STAGE_LAYOUT_LABELS.watch_party,
    pipDockVisible: true,
    gridViewEnabled: false,
    speakerCamEnabled: false,
  },
} as const satisfies Record<HostStageLayoutId, HostStageLayoutBehavior>;

export interface HostExperienceLane {
  id: HostExperienceMode;
  label: string;
  description: string;
}

export const HOST_PANEL_QUICK_ACTIONS = [
  'open-companion',
  'share-desktop',
  'toggle-reactions',
  'show-card',
  'start-recording',
  'launch-game',
  'share-gameplay',
  'save-replay',
  'add-live-media',
  'go-live',
] as const;

export type HostPanelQuickActionId = typeof HOST_PANEL_QUICK_ACTIONS[number];

export interface HostPanelQuickActionDefinition {
  id: HostPanelQuickActionId;
  label: string;
  targetTab: HostPanelTabId;
  requiredCapability?: HostPanelCapability;
  sourceFamily?: HostSourceFamily;
}

// Reactions are the shared emoji + targeted stage-feedback surface. When the
// room's compositor captures stage feedback, this capability is output-visible
// in recordings, streams, and simulcasts instead of being a local-only control.
export const HOST_PANEL_QUICK_ACTION_DEFINITIONS = {
  'open-companion': {
    id: 'open-companion',
    label: 'Companion',
    targetTab: 'companion',
    requiredCapability: 'supportsCompanionAgents',
    sourceFamily: 'companion',
  },
  'share-desktop': {
    id: 'share-desktop',
    label: 'Share Desktop',
    targetTab: 'stage',
    requiredCapability: 'supportsScreenShare',
    sourceFamily: 'desktop_share',
  },
  'toggle-reactions': {
    id: 'toggle-reactions',
    label: 'Reactions',
    targetTab: 'audience',
    requiredCapability: 'supportsReactions',
  },
  'show-card': {
    id: 'show-card',
    label: 'Show Card',
    targetTab: 'mode',
    requiredCapability: 'supportsPresentationOverlay',
    sourceFamily: 'presentation',
  },
  'start-recording': {
    id: 'start-recording',
    label: 'Record',
    targetTab: 'output',
    requiredCapability: 'supportsRecording',
  },
  'launch-game': {
    id: 'launch-game',
    label: 'Launch Game',
    targetTab: 'stage',
    requiredCapability: 'supportsRoomActivities',
    sourceFamily: 'room_activity',
  },
  'share-gameplay': {
    id: 'share-gameplay',
    label: 'Share Gameplay',
    targetTab: 'stage',
    requiredCapability: 'supportsGameplayShare',
    sourceFamily: 'gameplay_share',
  },
  'save-replay': {
    id: 'save-replay',
    label: 'Save Replay',
    targetTab: 'output',
    requiredCapability: 'supportsRecording',
    sourceFamily: 'gameplay_share',
  },
  'add-live-media': {
    id: 'add-live-media',
    label: 'Add Live Media',
    targetTab: 'stage',
    requiredCapability: 'supportsSharedStageMedia',
    sourceFamily: 'live_stream',
  },
  'go-live': {
    id: 'go-live',
    label: 'Go Live',
    targetTab: 'output',
    requiredCapability: 'supportsBroadcast',
  },
} as const satisfies Record<HostPanelQuickActionId, HostPanelQuickActionDefinition>;

export interface HostExperienceDefinition extends HostExperienceLane {
  defaultTab: HostPanelTabId;
  highlightedSections: readonly string[];
  collapsedSections: readonly string[];
  quickActions: readonly HostPanelQuickActionId[];
  defaultStageLayout?: HostStageLayoutId;
  defaultStageSourceFamily?: HostSourceFamily;
  sourceFamilies: readonly HostSourceFamily[];
}

export const HOST_EXPERIENCE_DEFINITIONS = {
  casual_conversation: {
    id: 'casual_conversation',
    label: 'Casual Conversation',
    description: 'People, companion, and lightweight participation.',
    defaultTab: 'stage',
    // #2838 — activities belong here too. Casual is the default experience in
    // standard rooms, so confining room_activity to Gaming meant a host saw no
    // games at all until they changed mode. Games are a casual-room staple.
    highlightedSections: ['people', 'layout', 'participation', 'reactions', 'activities'],
    collapsedSections: ['broadcast', 'simulcast', 'diagnostics'],
    quickActions: ['open-companion', 'share-desktop', 'toggle-reactions'],
    defaultStageLayout: 'conversation',
    defaultStageSourceFamily: 'people',
    sourceFamilies: ['people', 'companion', 'room_activity', 'desktop_share', 'shared_stage_media'],
  },
  presentation: {
    id: 'presentation',
    label: 'Presentation',
    description: 'Structured content, stage sources, and recording.',
    defaultTab: 'stage',
    highlightedSections: ['web_cards', 'media', 'reactions', 'record', 'broadcast'],
    collapsedSections: ['simulcast', 'diagnostics'],
    quickActions: ['share-desktop', 'toggle-reactions', 'show-card', 'start-recording'],
    defaultStageLayout: 'presentation',
    defaultStageSourceFamily: 'presentation',
    sourceFamilies: ['presentation', 'web_stage', 'shared_stage_media', 'desktop_share', 'people'],
  },
  gaming: {
    id: 'gaming',
    label: 'Gaming',
    description: 'Room-native games plus optional gameplay share.',
    defaultTab: 'stage',
    highlightedSections: ['activities', 'shares', 'reactions', 'record'],
    collapsedSections: ['web_cards', 'simulcast'],
    quickActions: ['launch-game', 'share-gameplay', 'toggle-reactions', 'save-replay'],
    defaultStageLayout: 'game',
    defaultStageSourceFamily: 'room_activity',
    sourceFamilies: ['room_activity', 'gameplay_share', 'desktop_share', 'people'],
  },
  watching_sports: {
    id: 'watching_sports',
    label: 'Watching Sports',
    description: 'Live media co-watch and shared viewing flow.',
    defaultTab: 'stage',
    highlightedSections: ['media', 'layout', 'reactions', 'broadcast'],
    collapsedSections: ['web_cards'],
    quickActions: ['add-live-media', 'toggle-reactions', 'go-live'],
    defaultStageLayout: 'watch_party',
    defaultStageSourceFamily: 'live_stream',
    sourceFamilies: ['live_stream', 'shared_stage_media', 'desktop_share', 'people'],
  },
} as const satisfies Record<HostExperienceMode, HostExperienceDefinition>;

export const HOST_EXPERIENCE_LANES = HOST_EXPERIENCE_MODES.map(
  (mode) => HOST_EXPERIENCE_DEFINITIONS[mode],
) satisfies readonly HostExperienceLane[];

/**
 * Every source family a room can deliver, regardless of lane (#2849, IA slice 4).
 *
 * `HostExperienceDefinition.sourceFamilies` used to gate which families existed
 * at all, which is why Casual showed no games: `room_activity` was listed only
 * under `gaming`. Patching the data (as #2839 did) fixes one instance and
 * leaves the mechanism, so the bug returns whenever a family is added and
 * someone forgets one of four lists.
 *
 * A lane now decides where you LAND and what is EXPANDED — never what exists.
 * Capability is the only gate on availability.
 */
export function getAvailableHostSourceFamilies(
  capabilities?: HostPanelCapabilitySet,
): HostSourceFamily[] {
  return (Object.keys(HOST_SOURCE_FAMILY_DEFINITIONS) as HostSourceFamily[])
    .filter((family) =>
      isHostPanelCapabilityEnabled(capabilities, HOST_SOURCE_FAMILY_DEFINITIONS[family].requiredCapability),
    );
}

/**
 * The lanes a room can actually offer.
 *
 * Standard rooms were offering a Presentation lane despite having neither
 * `supportsPresentationOverlay` nor `supportsWebStage` — it landed the host on
 * a tab whose content the room cannot produce. A lane is only meaningful if its
 * default stage source is something the room can deliver.
 */
export function getHostExperienceLanes(
  capabilities?: HostPanelCapabilitySet,
): HostExperienceLane[] {
  const available = new Set(getAvailableHostSourceFamilies(capabilities));
  return HOST_EXPERIENCE_LANES.filter((lane) => {
    const defaultFamily = lane.defaultStageSourceFamily;
    // A lane with no declared default is always offerable — it expresses
    // emphasis only, and has no content requirement of its own.
    return !defaultFamily || available.has(defaultFamily);
  });
}

export function getHostPanelQuickActionDefinitions(
  definition: HostExperienceDefinition,
  capabilities?: HostPanelCapabilitySet,
): HostPanelQuickActionDefinition[] {
  const enabledSourceFamilyIds = new Set(
    getHostSourceFamilyDefinitions(definition, capabilities).map(
      (sourceFamilyDefinition) => sourceFamilyDefinition.id,
    ),
  );

  return definition.quickActions
    .map((actionId) => HOST_PANEL_QUICK_ACTION_DEFINITIONS[actionId])
    .filter((actionDefinition) => {
      const sourceFamily = 'sourceFamily' in actionDefinition ? actionDefinition.sourceFamily : undefined;
      return (
        isHostPanelCapabilityEnabled(capabilities, actionDefinition.requiredCapability) &&
        (!sourceFamily || enabledSourceFamilyIds.has(sourceFamily))
      );
    });
}

export function getHostSourceFamilyDefinitions(
  definition: HostExperienceDefinition,
  capabilities?: HostPanelCapabilitySet,
): HostSourceFamilyDefinition[] {
  return definition.sourceFamilies
    .map((sourceFamily) => HOST_SOURCE_FAMILY_DEFINITIONS[sourceFamily])
    .filter((sourceFamilyDefinition) =>
      isHostPanelCapabilityEnabled(capabilities, sourceFamilyDefinition.requiredCapability),
    );
}

export function getHostSourceFamilyPreset(
  sourceFamily?: HostSourceFamily | null,
): HostSourceFamilyPreset | null {
  return sourceFamily ? HOST_SOURCE_FAMILY_PRESETS[sourceFamily] : null;
}

export function hasHostSourceFamily(
  definition: HostExperienceDefinition,
  capabilities: HostPanelCapabilitySet | undefined,
  sourceFamily: HostSourceFamily,
): boolean {
  return getHostSourceFamilyDefinitions(definition, capabilities).some(
    (sourceFamilyDefinition) => sourceFamilyDefinition.id === sourceFamily,
  );
}

export function getHostDefaultStageSourceFamily(
  definition: HostExperienceDefinition,
  capabilities?: HostPanelCapabilitySet,
): HostSourceFamily | null {
  const enabledSourceFamilies = getHostSourceFamilyDefinitions(definition, capabilities);
  if (definition.defaultStageSourceFamily) {
    const defaultSourceFamily = enabledSourceFamilies.find(
      (sourceFamilyDefinition) => sourceFamilyDefinition.id === definition.defaultStageSourceFamily,
    );
    if (defaultSourceFamily) return defaultSourceFamily.id;
  }
  return enabledSourceFamilies[0]?.id ?? null;
}

export function getHostPanelQuickActionSourceFamily(
  definition: HostExperienceDefinition,
  capabilities: HostPanelCapabilitySet | undefined,
  actionId: HostPanelQuickActionId,
): HostSourceFamily | null {
  const actionDefinition = HOST_PANEL_QUICK_ACTION_DEFINITIONS[actionId];
  const sourceFamily = 'sourceFamily' in actionDefinition ? actionDefinition.sourceFamily : undefined;
  if (!sourceFamily) return null;
  return hasHostSourceFamily(definition, capabilities, sourceFamily)
    ? sourceFamily
    : null;
}

export function getScreenShareModeForHostSourceFamily(
  sourceFamily?: HostSourceFamily | null,
): ScreenShareSourceMode | null {
  if (sourceFamily === 'gameplay_share') return 'gameplay';
  // 'window' (native-window picker intent) — a "Share Desktop" affordance must
  // accept app-window picks; 'screen' mode is browser-tab-only and rejects them.
  if (sourceFamily === 'desktop_share') return 'window';
  return null;
}

export function getHostStageLayoutBehavior(
  layoutId?: HostStageLayoutId,
): HostStageLayoutBehavior | null {
  return layoutId ? HOST_STAGE_LAYOUT_BEHAVIORS[layoutId] : null;
}

export function getHostStageLayoutLabel(layoutId?: HostStageLayoutId): string | null {
  return getHostStageLayoutBehavior(layoutId)?.label ?? null;
}

/**
 * Canonical host-panel sub-tabs (#2843, host-panel IA slice 1).
 *
 * ONE ordered set, shared by every room type. Before this, standard rooms and
 * townhall each invented their own sub-structure — standard had
 * People/Activities/Media/Shares, townhall had
 * Agenda/Presentations/Stage-media/Share/Config — with no shared vocabulary,
 * ordering, or components. A host who learned one room type had to relearn the
 * other, and the gaps between the two lists were where features went missing:
 * townhall's activities were startable by voice and absent from its panel
 * precisely because nothing said the two panels owed each other anything.
 *
 * Differences between room types are expressed ONLY as capabilities. Nothing
 * here branches on room type, and nothing downstream should either: a room that
 * cannot do something hides that sub-tab, it does not rename or reorder the
 * ones it keeps.
 */
export const HOST_PANEL_SUBTABS = [
  // Stage — every source that can be put on the stage.
  'people',
  'activities',
  'presentations',
  'program',
  'agenda',
  'share',
  'live',
  'library',
  // Output — what leaves the room.
  'record_stream',
] as const;

export type HostPanelSubTabId = typeof HOST_PANEL_SUBTABS[number];

export interface HostPanelSubTabDefinition {
  id: HostPanelSubTabId;
  label: string;
  /**
   * Owning top-level tab. Presentation/program/agenda belong to Mode;
   * general sources belong to Stage and recording belongs to Output.
   */
  tab: HostPanelTabId;
  /**
   * ANY-of, not all-of. Share is permitted by screen OR gameplay share, and
   * Record & Stream by recording OR broadcast — a room with only one of each
   * pair still has a real use for the sub-tab. An empty list means always
   * permitted.
   */
  requiredCapabilities: readonly HostPanelCapability[];
}

export const HOST_PANEL_SUBTAB_DEFINITIONS: Record<HostPanelSubTabId, HostPanelSubTabDefinition> = {
  people: {
    id: 'people',
    label: 'People',
    tab: 'stage',
    requiredCapabilities: [],
  },
  activities: {
    id: 'activities',
    label: 'Activities',
    tab: 'stage',
    requiredCapabilities: ['supportsRoomActivities'],
  },
  presentations: {
    id: 'presentations',
    label: 'Presentation',
    tab: 'mode',
    requiredCapabilities: ['supportsPresentationOverlay'],
  },
  program: {
    id: 'program',
    label: 'Program',
    tab: 'mode',
    requiredCapabilities: ['supportsProgramFeed'],
  },
  agenda: {
    id: 'agenda',
    label: 'Agenda',
    tab: 'mode',
    requiredCapabilities: ['supportsProgramFeed'],
  },
  share: {
    id: 'share',
    label: 'Share',
    tab: 'stage',
    requiredCapabilities: ['supportsScreenShare', 'supportsGameplayShare'],
  },
  live: {
    id: 'live',
    label: 'Live',
    tab: 'stage',
    requiredCapabilities: ['supportsSharedStageMedia'],
  },
  library: {
    id: 'library',
    label: 'Library',
    tab: 'stage',
    requiredCapabilities: ['supportsSharedStageMedia'],
  },
  record_stream: {
    id: 'record_stream',
    label: 'Record & Stream',
    tab: 'output',
    requiredCapabilities: ['supportsRecording', 'supportsBroadcast'],
  },
};

/**
 * The sub-tabs a room may show for a top-level tab, in canonical order.
 *
 * Order comes from HOST_PANEL_SUBTABS rather than from the caller, so no room
 * type can reorder them — consistent position is most of what makes a shared
 * vocabulary worth having.
 */
export function getHostPanelSubTabs(
  tab: HostPanelTabId,
  capabilities?: HostPanelCapabilitySet,
): HostPanelSubTabDefinition[] {
  return HOST_PANEL_SUBTABS
    .map((id) => HOST_PANEL_SUBTAB_DEFINITIONS[id])
    .filter((definition) => definition.tab === tab)
    .filter((definition) =>
      definition.requiredCapabilities.length === 0
      || definition.requiredCapabilities.some((capability) =>
        isHostPanelCapabilityEnabled(capabilities, capability),
      ),
    );
}
